import AdminPageLoader from "components/AdminPageLoader";
import { portalPageMeta } from "layouts/Admin2/pageMeta";
import PageHeading from "admin/components/PageHeading";
import useCurrencies from "api/CurrencyResource/useCurrencies";
import usePayPublisher from "api/PublisherPayments/usePayPublisher";
import useFetchPublishers from "api/PublisherResource/useFetchPublishers";
import FormField from "components/v2/FormField";
import { Form, Formik } from "formik";
import Admin2 from "layouts/Admin2";
import React from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { IoIosArrowForward } from "react-icons/io";
import { activeCurrencies, getActiveCurrencyCodeLabel } from "utils";
import * as Yup from "yup";

const PayPublisher = () => {
    const { data: catalog = [], isLoading: currenciesLoading, isError: currenciesError, refetch: retryCurrencies } = useCurrencies(true);
	const { data: publishers, isLoading: isLoadingPublishers } =
		useFetchPublishers();

	const initialValues = {
		publisher_id: "",
		amount: "",
		currency: activeCurrencies.USD.code,
		description: "",
		reference: "",
	};

	const validate = Yup.object().shape({
		publisher_id: Yup.string().required("Publisher is required"),
		amount: Yup.number()
			.required("Amount is required")
			.moreThan(0, "Amount should be greater than 0"),
		currency: Yup.string().required("Currency is required"),
	});

	const { mutateAsync, isLoading } = usePayPublisher();

	const onSubmit = async (values) => {
		await mutateAsync(values);
	};

	if (isLoadingPublishers) return <AdminPageLoader />;

	return (
		<Admin2>
            {currenciesError && <p role="alert" className="text-red-700">Unable to load payout currencies. <button type="button" onClick={() => retryCurrencies()} className="underline">Retry</button></p>}
			<Formik
				initialValues={initialValues}
				validationSchema={validate}
				onSubmit={onSubmit}
			>
				{(formik) => {
					const currencyLabel = formik.values.currency
						? `(in ${getActiveCurrencyCodeLabel(formik.values.currency)})`
						: "";

					return (
						<Form className="portal-payout-form">
							<div className="mb-8">
								<PageHeading title="Publisher payouts" description={portalPageMeta("/pay-publisher").description} />
								<p>Required field are marked with (*)</p>
							</div>

							<div className="space-y-6">
								<FormField
									type="select"
									name="currency"
									label="Currency *"
									options={catalog.map((currency) => ({ label: `${currency.label} (${currency.code})`, value: currency.code }))}
                                    isLoading={currenciesLoading} disabled={currenciesLoading || currenciesError}
								/>

								<FormField
									name="amount"
									type="number"
									min="0"
									step={10 ** -(catalog.find((currency) => currency.code === formik.values.currency)?.decimal_places ?? 2)}
									label={`Amount to be paid ${currencyLabel} *`}
								/>

								<FormField
									type="select"
									name="publisher_id"
									label="Select publisher to pay *"
									options={publishers?.data?.map((publisher) => ({
										label: publisher.name,
										value: publisher.id,
									}))}
								/>

								<FormField
									type="text"
									name="reference"
									label="Reference (e.g bank transaction reference)"
								/>

								<FormField
									type="textarea"
									name="description"
									label="Description"
								/>

								<div className=" flex ">
									<button
										type="submit"
										className="px-4 py-2 rounded-full bg-akgreenest text-white flex gap-1 items-center justify-center "
									>
										{isLoading ? (
											<>
												Processing payment{" "}
												<AiOutlineLoading3Quarters
													size={20}
													className="animate-spin"
												/>
											</>
										) : (
											<>
												Proceed with payment <IoIosArrowForward size={20} />
											</>
										)}
									</button>
								</div>
							</div>
						</Form>
					);
				}}
			</Formik>
		</Admin2>
	);
};

export default PayPublisher;
